import React, { useEffect, useRef } from 'react'
import { configureApi } from './constants/configureApi'
import RoutesComponent from './routing/Routes'
import './global.css'
import Paho from 'paho-mqtt'
import moment from 'moment'
import { store } from './redux/store'
import { useDispatch } from 'react-redux'
import { authRequestStart } from './redux/actions/Auth'
import { menuItemPatch, menuRequestStart } from './redux/actions/Menu'
import whoosh from './sound/whoosh.mp3'

export const api = configureApi(store)

const MENU_REFRESH_DEBOUNCE = 2000
const MENU_POLL_INTERVAL = 90000
// Заказы кухне нужны быстро, а MQTT-пуш может молча потеряться (QoS 0, обрыв
// сокета, сон устройства). Поэтому поверх пуша держим частый опрос заказов —
// это гарантирует, что новый заказ появится максимум через этот интервал даже
// без единого пуша.
const ORDERS_POLL_INTERVAL = 15000
const MQTT_RECONNECT_DELAY = 3000

const MQTT_HOST = 'api.menu-rest.com'

const playSound = src => {
  try {
    const audio = new Audio(src)
    const played = audio.play()
    if (played && typeof played.catch === 'function') played.catch(() => {})
  } catch (e) {}
}

const App = () => {
  const pathname = window.location.pathname?.split('/')[1]
  const dispatch = useDispatch()
  const menuRefreshTimer = useRef(null)

  const fetchMenu = () => {
    if (pathname) {
      dispatch(menuRequestStart({ restId: pathname, date: moment().format('YYYY-MM-DD') }))
    }
  }

  const fetchOrders = () => {
    if (pathname) {
      dispatch(authRequestStart(pathname))
    }
  }

  const scheduleMenuRefresh = () => {
    clearTimeout(menuRefreshTimer.current)
    menuRefreshTimer.current = setTimeout(fetchMenu, MENU_REFRESH_DEBOUNCE)
  }

  // Опрос + рефетч при возврате вкладки/сети — единственная защита от
  // рассинхронизации, если MQTT-пуш не дошёл. Работает всегда, независимо от MQTT.
  useEffect(() => {
    if (!pathname) return undefined

    fetchOrders()
    fetchMenu()

    const ordersPoll = setInterval(fetchOrders, ORDERS_POLL_INTERVAL)
    const menuPoll = setInterval(fetchMenu, MENU_POLL_INTERVAL)

    const resync = () => {
      if (document.visibilityState === 'visible') {
        fetchOrders()
        fetchMenu()
      }
    }
    document.addEventListener('visibilitychange', resync)
    window.addEventListener('online', resync)

    return () => {
      clearInterval(ordersPoll)
      clearInterval(menuPoll)
      clearTimeout(menuRefreshTimer.current)
      document.removeEventListener('visibilitychange', resync)
      window.removeEventListener('online', resync)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  // MQTT: мгновенные обновления заказов и меню. Сокет может отвалиться молча —
  // поэтому переподключаемся сами, а после каждого (пере)подключения делаем
  // полный рефетч, чтобы подобрать всё, что пропустили за время простоя.
  useEffect(() => {
    if (!pathname) return undefined

    let client = null
    let reconnectTimer = null
    let stopped = false

    // Бэкенд шлёт orderStatus 'sent' и при поступлении заказа, и при отметке
    // блюда готовым/закрытии — статус один и тот же. Звук нужен только на приход,
    // поэтому играем его лишь на первый 'sent' по заказу, повторные — молча.
    // Заказы с киоска (QR-оплата) попадают в сырой список ещё до подтверждения
    // оплаты, с пустым item.sent (см. IndexScene) — их в «известные» не берём,
    // иначе настоящий приход (когда оплата подтвердится) останется без звука.
    const knownOrderIds = new Set(
      (store.getState().orderData || []).filter(item => item && item.sent).map(item => item.id).filter(id => id != null)
    )

    const scheduleReconnect = () => {
      if (stopped) return
      clearTimeout(reconnectTimer)
      reconnectTimer = setTimeout(connect, MQTT_RECONNECT_DELAY)
    }

    const handleOrderMessage = payload => {
      if (payload.orderStatus === 'sent') {
        const orderId = payload.orderId
        if (orderId == null || !knownOrderIds.has(orderId)) {
          playSound(whoosh)
          if (orderId != null) knownOrderIds.add(orderId)
        }
      }

      fetchOrders()
    }

    const handleMenuMessage = payload => {
      if (payload.event === 'stoplist') {
        dispatch(
          menuItemPatch({
            menuId: payload.menuId,
            patch: { stopped: payload.stopped, quantity: payload.quantity, quantity_left: payload.quantityLeft },
          })
        )
      } else if (payload.event === 'refresh') {
        scheduleMenuRefresh()
      }
    }

    function connect() {
      client = new Paho.Client(`wss://${MQTT_HOST}/ws`, `kitchen${Date.now()}`)

      client.onConnectionLost = responseObject => {
        if (responseObject.errorCode !== 0) {
          console.log('orders mqtt lost:', responseObject.errorMessage)
        }
        scheduleReconnect()
      }

      client.onMessageArrived = message => {
        let payload
        try {
          payload = JSON.parse(message.payloadString)
        } catch (e) {
          return
        }
        if (message.destinationName === `/api/menu/${pathname}`) {
          handleMenuMessage(payload)
        } else {
          handleOrderMessage(payload)
        }
      }

      try {
        client.connect({
          timeout: 3,
          cleanSession: true,
          keepAliveInterval: 20,
          userName: 'react',
          useSSL: true,
          password: 'LBanrB2ku89q',
          onSuccess: () => {
            client.subscribe(`/api/orders/${pathname}`, { qos: 0 })
            client.subscribe(`/api/menu/${pathname}`, { qos: 0 })
            console.log('orders mqtt connected')
            // Могли пропустить пуши, пока сокет был мёртв — синхронизируемся.
            fetchOrders()
            fetchMenu()
          },
          onFailure: err => {
            console.log('orders mqtt connect failed', err && err.errorMessage)
            scheduleReconnect()
          },
        })
      } catch (e) {
        console.log('orders mqtt connect threw', e && e.message)
        scheduleReconnect()
      }
    }

    connect()

    return () => {
      stopped = true
      clearTimeout(reconnectTimer)
      if (client) {
        try {
          client.disconnect()
        } catch (e) {}
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  return <RoutesComponent />
}

export default App
